import { useState, useEffect } from "react";
import { Smartphone, Download, Bell, X, Check, Share } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { canPromptInstall, isInstalled, promptInstall, subscribePush } from "../lib/pwa";
import { useLang } from "../i18n";

const COPY = {
  fr: {
    eyebrow: "Application mobile",
    title: "DOC TOGO dans votre poche.",
    text: "Installez l'application Android en 10 secondes, directement depuis votre navigateur : démarrez vos démarches, suivez vos dossiers en temps réel et recevez une notification à chaque avancement.",
    features: [
      "Aucun store requis — installation directe",
      "Suivi de vos dossiers en temps réel",
      "Notifications push à chaque étape",
    ],
    download: "Télécharger l'application",
    installed: "Application installée",
    notif: "Activer les notifications",
    toastInstalled: "L'application est déjà installée sur cet appareil.",
    toastSuccess: "Application installée ! Activez les notifications pour suivre vos dossiers.",
    toastNotifOk: "Notifications activées",
    toastNotifKo: "Autorisez les notifications dans votre navigateur",
    helpTitle: "Installer l'application Android",
    helpSteps: [
      <>Ouvrez ce site dans <strong>Chrome</strong> sur votre téléphone Android.</>,
      <>Touchez le menu <strong>⋮</strong> en haut à droite.</>,
      <>Choisissez <strong>« Ajouter à l'écran d'accueil »</strong> puis « Installer ».</>,
    ],
    helpNote: "L'icône DOC TOGO apparaît sur votre écran d'accueil, comme une application classique.",
  },
  en: {
    eyebrow: "Mobile app",
    title: "DOC TOGO in your pocket.",
    text: "Install the Android app in 10 seconds, straight from your browser: start your procedures, track your cases in real time and get a notification at every step.",
    features: [
      "No app store required — direct install",
      "Real-time case tracking",
      "Push notifications at every step",
    ],
    download: "Download the app",
    installed: "App installed",
    notif: "Enable notifications",
    toastInstalled: "The app is already installed on this device.",
    toastSuccess: "App installed! Enable notifications to track your cases.",
    toastNotifOk: "Notifications enabled",
    toastNotifKo: "Allow notifications in your browser",
    helpTitle: "Install the Android app",
    helpSteps: [
      <>Open this site in <strong>Chrome</strong> on your Android phone.</>,
      <>Tap the <strong>⋮</strong> menu at the top right.</>,
      <>Choose <strong>"Add to Home screen"</strong> then "Install".</>,
    ],
    helpNote: "The DOC TOGO icon appears on your home screen, like a regular app.",
  },
};

const AppCta = () => {
  const { lang } = useLang();
  const c = COPY[lang];
  const [installable, setInstallable] = useState(canPromptInstall());
  const [installed, setInstalled] = useState(isInstalled());
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onReady = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener("pwa-prompt-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-prompt-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleDownload = async () => {
    if (installed) {
      toast.success(c.toastInstalled);
      return;
    }
    setBusy(true);
    try {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success(c.toastSuccess);
        subscribePush().catch(() => {});
      } else if (outcome === null) {
        setShowHelp(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="app-cta-section" className="relative overflow-hidden bg-ivory py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-deep p-10 text-white lg:p-16">
            <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-gold/15 blur-[100px]" />
            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">{c.eyebrow}</span>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-balance sm:text-4xl">
                  {c.title}
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-white/60">{c.text}</p>
                <ul className="mt-6 space-y-3">
                  {c.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-white/75">
                      <Check className="h-4 w-4 shrink-0 text-gold" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={handleDownload}
                    disabled={busy}
                    data-testid="app-download-btn"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    <Download className="h-5 w-5 transition-transform duration-300 group-hover:translate-y-0.5" />
                    {installed ? c.installed : c.download}
                  </button>
                  <button
                    onClick={() => subscribePush().then((ok) => toast[ok ? "success" : "error"](ok ? c.toastNotifOk : c.toastNotifKo))}
                    data-testid="app-notif-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-white/10"
                  >
                    <Bell className="h-5 w-5 text-gold" /> {c.notif}
                  </button>
                </div>
              </div>
              <div className="hidden justify-center lg:flex">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-[3rem] border border-gold/25 rotate-3" />
                  <div className="relative grid h-72 w-40 place-items-center rounded-[2.5rem] border-4 border-white/10 bg-brand shadow-2xl">
                    <img src="/icon-192.png" alt="Application DOC TOGO" className="h-20 w-20 rounded-2xl shadow-lg" />
                    <span className="absolute bottom-8 font-display text-xs font-bold uppercase tracking-widest text-white/60">DOC TOGO</span>
                    <Smartphone className="absolute -bottom-5 -right-5 h-12 w-12 rounded-2xl bg-gold p-2.5 text-brand-deep shadow-xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {showHelp && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-brand-deep/70 px-6 backdrop-blur-sm" onClick={() => setShowHelp(false)}>
          <div data-testid="app-install-help" className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold text-slate-900">{c.helpTitle}</h3>
              <button onClick={() => setShowHelp(false)} data-testid="app-install-help-close" className="rounded-full p-2 text-slate-400 hover:bg-slate-100" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ol className="mt-5 space-y-4 text-sm leading-relaxed text-slate-600">
              {c.helpSteps.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand font-mono text-xs font-bold text-white">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <p className="mt-5 rounded-xl bg-brand-light/60 p-4 text-xs leading-relaxed text-slate-600">{c.helpNote}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default AppCta;
